import { useCallback, useEffect, useState } from 'react';
import { IconButton, InputAdornment } from '@material-ui/core';
import IconVisibility from '@material-ui/icons/Visibility';
import IconVisibilityOff from '@material-ui/icons/VisibilityOff';
import PropTypes from 'prop-types';

import { CUSTOM_BUTTON_SIZE, CustomButton } from 'src/components/CustomButton';
import CustomTextField from 'src/components/CustomTextField';
import { isValidErrors, useValidatedForm } from 'src/lib/hooks';
import { RULES } from 'src/lib/validation';

import { RegistrationError } from './RegistrationError';
import { useStyles } from './useStyles';

export const RegistrationForm = ({ classes, loading, error, onChange, onRegister }) => {
  const styles = useStyles({ classes });

  // Form state
  const [showPassword, setShowPassword] = useState(false);
  const toggleShowPassword = useCallback(() => {
    setShowPassword((prev) => !prev);
  }, []);
  const [showErrors, setShowErrors] = useState(true);

  // If we receive new 'error' - show it
  useEffect(() => {
    if (error) {
      setShowErrors(true);
    }
  }, [error]);

  // Form state
  const {
    fields: formFields,
    errors: formErrors,
    validateWithRules: validateForm,
    update: updateForm,
  } = useValidatedForm({
    initialFields: {
      email: '',
      password: '',
    },
    rules: {
      email: { [RULES.required]: true, [RULES.is_email]: true },
      password: { [RULES.password]: true },
    },
  });

  // Run authentication
  const handleSubmit = useCallback(
    (event) => {
      event.preventDefault();

      setShowErrors(true);
      const validations = validateForm();
      if (!isValidErrors(validations)) {
        return;
      }

      onRegister(formFields.email, formFields.password);
    },
    [validateForm, formFields, onRegister]
  );

  const updateField = useCallback(
    (field, value) => {
      setShowErrors(false);
      updateForm(field, value);

      onChange(field, value);
    },
    [updateForm, onChange]
  );

  return (
    <form
      className={styles.root}
      onSubmit={handleSubmit}
      data-testid="RegistrationForm"
    >
      {showErrors && error && (
        <RegistrationError
          className={styles.errorPanel}
          error={error}
        />
      )}

      <CustomTextField
        autoFocus
        className={styles.field}
        id="email"
        value={formFields.email}
        onChange={(event) => updateField('email', event.target.value)}
        error={showErrors && Boolean(formErrors.email)}
        helperText={showErrors && formErrors.email}
        label="Work email"
        margin="normal"
        variant="outlined"
        fullWidth
        dataTestId="registrationFormEmail"
        InputLabelProps={{
          'data-testid': 'RegistrationFormEmailLabel',
          classes: { root: styles.label },
        }}
      />

      <CustomTextField
        className={styles.field}
        id="password"
        type={showPassword ? 'text' : 'password'}
        value={formFields.password}
        onChange={(event) => updateField('password', event.target.value)}
        error={showErrors && Boolean(formErrors.password)}
        helperText={showErrors && formErrors.password}
        label="Password"
        margin="normal"
        variant="outlined"
        fullWidth
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={toggleShowPassword}
                data-testid="RegistrationForm__VisibilityButton"
              >
                {showPassword ? <IconVisibilityOff /> : <IconVisibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
        disallowLogRocket
        dataTestId="registrationFormPassword"
        InputLabelProps={{
          'data-testid': 'RegistrationFormPasswordLabel',
          classes: { root: styles.label },
        }}
      />

      <CustomButton
        className={styles.button}
        loading={loading}
        disabled={loading}
        size={CUSTOM_BUTTON_SIZE.large}
        type="submit"
        data-testid="registrationFormSubmitButton"
        fullWidth
      >
        Sign up
      </CustomButton>
    </form>
  );
};

RegistrationForm.propTypes = {
  classes: PropTypes.shape({}),
  loading: PropTypes.bool,
  error: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onRegister: PropTypes.func.isRequired,
};

RegistrationForm.defaultProps = {
  classes: null,
  loading: null,
  error: null,
};
